import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../services/api';
import { useAuth } from '../context/AuthContext';
import SEO from '../components/SEO';
import { Loader2, AlertCircle, User, Search, Download, Calendar } from 'lucide-react';

const monthKeyFromDate = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

const monthLabelFromKey = (monthKey) => {
  if (!monthKey || monthKey === 'all') return 'All time';
  const [year, month] = monthKey.split('-').map(Number);
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(new Date(year, month - 1, 1));
};

const AdminUsers = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [exporting, setExporting] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState('all');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const res = await axios.get(API.GET_ALL_USERS, { withCredentials: true });
        if (res.data && res.data.success) {
          setUsers(res.data.users || []);
        } else {
          setError(res.data?.message || 'Failed to load users');
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load users');
      } finally {
        setLoading(false);
      }
    };

    if (user && user.role === 'admin') {
      fetchUsers();
    } else {
      setLoading(false);
      setError('Unauthorized: admin only');
    }
  }, [user]);

  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (!searchQuery.trim()) return true;

      const query = searchQuery.toLowerCase();
      const fullName = `${u.firstname || ''} ${u.lastname || ''}`.toLowerCase();
      const email = (u.email || '').toLowerCase();
      const college = (u.college || '').toLowerCase();
      const branch = (u.branch || '').toLowerCase();
      const phone = (u.phone || '').toLowerCase();
      const userId = (u._id || '').toLowerCase();

      return (
        fullName.includes(query) ||
        email.includes(query) ||
        college.includes(query) ||
        branch.includes(query) ||
        phone.includes(query) ||
        userId.includes(query)
      );
    });
  }, [searchQuery, users]);

  const referralStats = useMemo(() => {
    return users.reduce((acc, currentUser) => {
      const source = currentUser.referralSource || 'Not Specified';
      acc[source] = (acc[source] || 0) + 1;
      return acc;
    }, {});
  }, [users]);

  const referralData = useMemo(
    () => Object.entries(referralStats)
      .map(([source, count]) => ({ source, count }))
      .sort((a, b) => b.count - a.count),
    [referralStats]
  );

  const totalUsers = users.length;

  const monthlyUsersMap = useMemo(() => users.reduce((acc, currentUser) => {
    const monthKey = monthKeyFromDate(currentUser.createdAt);
    if (!monthKey) return acc;

    if (!acc[monthKey]) {
      acc[monthKey] = 0;
    }

    acc[monthKey] += 1;
    return acc;
  }, {}), [users]);

  const monthTabs = useMemo(() => ([
    { key: 'all', label: 'All time' },
    ...Object.keys(monthlyUsersMap)
      .sort((a, b) => b.localeCompare(a))
      .map((monthKey) => ({ key: monthKey, label: monthLabelFromKey(monthKey) })),
  ]), [monthlyUsersMap]);

  const selectedMonthUsers = selectedMonth === 'all' ? totalUsers : (monthlyUsersMap[selectedMonth] || 0);
  const monthIndex = monthTabs.findIndex((month) => month.key === selectedMonth);
  const previousMonthKey = monthIndex > 1 ? monthTabs[monthIndex - 1]?.key : null;
  const previousMonthUsers = previousMonthKey && previousMonthKey !== 'all'
    ? (monthlyUsersMap[previousMonthKey] || 0)
    : null;

  useEffect(() => {
    if (selectedMonth === 'all') return;
    if (monthTabs.length === 1) return;
    const hasSelectedMonth = monthTabs.some((month) => month.key === selectedMonth);
    if (!hasSelectedMonth) {
      setSelectedMonth(monthTabs[1]?.key || 'all');
    }
  }, [monthTabs, selectedMonth]);

  const handleExportUsers = async () => {
    try {
      setExporting(true);
      const res = await axios.get(API.EXPORT_USERS, {
        withCredentials: true,
        responseType: 'blob',
      });

      const blob = new Blob([res.data], {
        type: res.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `users-export-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to export users');
    } finally {
      setExporting(false);
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-300 text-lg font-medium">Loading users...</p>
        </div>
      </div>
    );

  if (error)
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 px-4">
        <div className="text-center max-w-md">
          <div className="p-3 rounded-full bg-red-500/10 border border-red-500/20 w-fit mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Error Loading Users</h2>
          <p className="text-red-400">{error}</p>
        </div>
      </div>
    );

  return (
    <>
      <SEO 
        title="Users - Admin Panel | ReferMe"
        description="Admin panel for viewing and managing users."
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-950 text-white">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <header className="relative overflow-hidden rounded-2xl border border-gray-800 bg-gradient-to-br from-gray-900/80 via-gray-900/60 to-gray-900/80 backdrop-blur-xl p-6 sm:p-8 mb-8 shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-pink-500/5 pointer-events-none" />
          <div className="relative flex items-center gap-4">
            <div className="p-3 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
              <User className="w-6 h-6 text-indigo-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">All Users</h1>
              <p className="text-sm text-gray-400">List of registered users and recent activity</p>
            </div>
            <button
              onClick={handleExportUsers}
              disabled={exporting}
              className="ml-auto inline-flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2 text-sm font-medium text-emerald-300 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Download className={`w-4 h-4 ${exporting ? 'animate-pulse' : ''}`} />
              {exporting ? 'Exporting...' : 'Download Excel'}
            </button>
          </div>
        </header>

        <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-6 mb-6 shadow-xl">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-5">
            <div>
              <h2 className="text-xl font-bold text-white mb-1">Monthly New Users</h2>
              <p className="text-sm text-gray-400">Switch months to monitor user growth</p>
            </div>
            <div className="sm:ml-auto inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 text-sm">
              <Calendar className="w-4 h-4" />
              {monthLabelFromKey(selectedMonth)}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 mb-6 overflow-x-auto pb-1">
            {monthTabs.map((month) => (
              <button
                key={month.key}
                onClick={() => setSelectedMonth(month.key)}
                className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-all duration-300 border ${
                  selectedMonth === month.key
                    ? 'bg-indigo-500 text-white border-indigo-400 shadow-lg shadow-indigo-500/20'
                    : 'bg-gray-800/60 text-gray-300 border-gray-700 hover:bg-gray-700/80'
                }`}
              >
                {month.label}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="rounded-lg border border-gray-700/60 bg-gray-800/40 p-4">
              <p className="text-xs text-gray-400 mb-1">New Users</p>
              <p className="text-2xl font-bold text-blue-400">{selectedMonthUsers}</p>
              <p className="text-xs text-gray-500 mt-1">{selectedMonth === 'all' ? 'All registered users' : 'Users joined in selected month'}</p>
            </div>

            <div className="rounded-lg border border-gray-700/60 bg-gray-800/40 p-4">
              <p className="text-xs text-gray-400 mb-1">Total Users</p>
              <p className="text-2xl font-bold text-white">{totalUsers}</p>
              <p className="text-xs text-gray-500 mt-1">All accounts in the system</p>
            </div>

            <div className="rounded-lg border border-gray-700/60 bg-gray-800/40 p-4">
              <p className="text-xs text-gray-400 mb-1">Month Over Month</p>
              <p className="text-2xl font-bold text-emerald-400">
                {selectedMonth === 'all' || previousMonthUsers === null
                  ? '—'
                  : previousMonthUsers === 0
                    ? 'N/A'
                    : `${selectedMonthUsers >= previousMonthUsers ? '+' : ''}${(((selectedMonthUsers - previousMonthUsers) / previousMonthUsers) * 100).toFixed(1)}%`}
              </p>
              <p className="text-xs text-gray-500 mt-1">Compared to previous month</p>
            </div>
          </div>
        </div>

        {/* Referral Source Statistics */}
        <div className="rounded-xl border border-gray-800 bg-gray-900/40 backdrop-blur-sm p-6 mb-6 shadow-xl">
          <div className="flex items-center gap-2 mb-4">
            <svg className="w-5 h-5 text-pink-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
            </svg>
            <h2 className="text-lg font-bold text-white">User Acquisition Sources</h2>
            <span className="ml-auto text-sm text-gray-400">{totalUsers} total users</span>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {referralData.map(({ source, count }) => {
              const percentage = ((count / totalUsers) * 100).toFixed(1);
              const colorMap = {
                'Friend/Classmate': 'from-blue-500 to-cyan-500',
                'Social Media': 'from-pink-500 to-rose-500',
                'WhatsApp Group': 'from-green-500 to-emerald-500',
                'College Notice': 'from-yellow-500 to-orange-500',
                'Poster': 'from-purple-500 to-violet-500',
                'Google Search': 'from-indigo-500 to-blue-500',
                'YouTube': 'from-red-500 to-pink-500',
                'Teacher/Professor': 'from-teal-500 to-cyan-500',
                'Other': 'from-gray-500 to-slate-500',
                'Not Specified': 'from-gray-600 to-gray-700'
              };
              const gradient = colorMap[source] || 'from-gray-500 to-gray-600';
              
              return (
                <div key={source} className="relative overflow-hidden rounded-lg border border-gray-800 bg-gray-800/40 p-4 hover:bg-gray-800/60 transition-all group">
                  <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-5 group-hover:opacity-10 transition-opacity`}></div>
                  <div className="relative">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-xs font-medium text-gray-400 truncate">{source}</p>
                      <span className="text-xs font-bold text-gray-500">{percentage}%</span>
                    </div>
                    <p className="text-2xl font-bold text-white mb-2">{count}</p>
                    <div className="w-full h-1.5 bg-gray-700/50 rounded-full overflow-hidden">
                      <div 
                        className={`h-full bg-gradient-to-r ${gradient} rounded-full transition-all duration-500`}
                        style={{ width: `${percentage}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-6">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, college, branch, phone, or user ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
              >
                <span className="text-lg">×</span>
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="text-xs text-gray-400 mt-2 ml-1">
              Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''}
            </p>
          )}
        </div>

        <div className="overflow-x-auto rounded-lg border border-gray-800 bg-gray-900/40 p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-left">
                <th className="py-2 px-3">Name</th>
                <th className="py-2 px-3">Email</th>
                <th className="py-2 px-3">College</th>
                <th className="py-2 px-3">Branch / Year</th>
                <th className="py-2 px-3">Mobile</th>
                <th className="py-2 px-3">Last Login</th>
                <th className="py-2 px-3">Created</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(u => (
                <tr key={u._id} className="border-t border-gray-800/60">
                  <td className="py-3 px-3">
                    <div className="text-sm font-medium text-white">{u.firstname || '-'} {u.lastname || ''}</div>
                    <div className="text-xs text-gray-400">{u._id}</div>
                  </td>
                  <td className="py-3 px-3 text-sm text-gray-300">{u.email}</td>
                  <td className="py-3 px-3 text-sm text-gray-300">{u.college || '-'}</td>
                  <td className="py-3 px-3 text-sm text-gray-300">{u.branch || '-'} / {u.year || '-'}</td>
                  <td className="py-3 px-3 text-sm text-gray-300">{u.phone || '-'}</td>
                  <td className="py-3 px-3 text-sm text-gray-300">{u.lastLogin ? new Date(u.lastLogin).toLocaleString() : '-'}</td>
                  <td className="py-3 px-3 text-sm text-gray-300">{u.createdAt ? new Date(u.createdAt).toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {filteredUsers.length === 0 && (
            <div className="py-6 text-center text-gray-400">
              {searchQuery ? 'No users match your search' : 'No users found'}
            </div>
          )}
        </div>
      </section>
    </div>
    </>
  );
};

export default AdminUsers;
